import { ReactNode } from 'react';
import { Button, Spinner, Container } from 'react-bootstrap';
import { useAuth } from '@/contexts/AuthContext';
import LoginForm from './LoginForm';
import ReauthDialog from './ReauthDialog';
import SessionGuard from './SessionGuard';

interface ProtectedRouteProps {
  children: ReactNode;
  requireRole?: string;
}

function FullPageSpinner({ text }: { text: string }) {
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="text-center">
        <Spinner animation="border" variant="primary" />
        <div className="mt-3 text-muted">{text}</div>
      </div>
    </div>
  );
}

export default function ProtectedRoute({ children, requireRole }: ProtectedRouteProps) {
  const { isAuthenticated, loading, signingOut, hasRole, signOut } = useAuth();

  // A session is ending. The page reloads once the session is revoked.
  if (signingOut) {
    return <FullPageSpinner text="Signing you out..." />;
  }

  // Show loading spinner while checking authentication
  if (loading) {
    return <FullPageSpinner text="Loading..." />;
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <LoginForm />;
  }

  // Check role-based access if required
  if (requireRole && !hasRole(requireRole)) {
    return (
      <Container className="mt-5">
        <SessionGuard />
        <ReauthDialog />
        <div className="text-center">
          <h3>You cannot use the portal yet</h3>
          <p className="text-muted">
            Your account is not in the <strong>{requireRole}</strong> group. Ask a portal admin to add you.
          </p>
          <p className="text-muted">If they added you a moment ago, sign out, then sign in again.</p>
          <Button variant="outline-primary" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <>
      <SessionGuard />
      <ReauthDialog />
      {children}
    </>
  );
}
